import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Button } from '../../../../../components/common/buttons/Button';
import { Icon } from '../../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../../components/common/modal/popup/PopupConfirmModal';
import { sellTractorPageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { TEMP_DATA_KEYS } from '../../../../../constants/tempData/tempData.contant';
import { useAppDispatch } from '../../../../../hooks/store/useStore';
import { useUserTempData } from '../../../../../hooks/userTempData/useUserTempData';
import { IUserSellTractorTempData } from '../../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../../store/userSellTractorSurveySlice';

export const ContinueWithTempData = () => {
  const navigate = useNavigate();

  const { getUserTempData } = useUserTempData();

  const { userTempData } = getUserTempData();
  const [userSellTractorTempData, setUserSellTractorTempData] = useState<IUserSellTractorTempData>();

  const [isShowDataCheckModal, setIsShowDataCheckModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  useEffect(() => {
    const savedSellTractorData: string | undefined = userTempData.get(TEMP_DATA_KEYS.SELL_TRACTOR_SURVEY);

    if (savedSellTractorData) {
      const parsedSellTractorData: IUserSellTractorTempData = JSON.parse(savedSellTractorData);
      setUserSellTractorTempData(parsedSellTractorData);
    }
  }, []);

  if (!userSellTractorTempData) {
    return <></>;
  }

  const { formData } = userSellTractorTempData;
  const { brand, model, engineOutputInPs } = formData;

  const handleContinueRegistration = () => {
    ampli.pvmClickSellTractorTempOwnConfirm();
    ReactGA.event('pvm_click_sell_tractor_temp_own_confirm');
    dispatch(userSellTractorActions.changeAllStates(formData));
    navigate(sellTractorPageInfo.overviewPage.path, { replace: true });
  };

  const continueWithEngineOutputInPsText = engineOutputInPs ? `(${engineOutputInPs}마력)` : '';

  return (
    <>
      <Container>
        <IconBackground>
          <Icon name={'TractorFront'} size={'XLarge'} />
        </IconBackground>
        <TextSectionContainer>
          <BrandAndModel>{`${brand} ${model}`}</BrandAndModel>
          <Description>{'임시 저장 중이예요'}</Description>
        </TextSectionContainer>
        <ButtonWrapper>
          <Button
            borderType={'round'}
            color={'filledPrimaryStrong'}
            size={'xs'}
            text={{ size: 'sm', content: '이어서 등록' }}
            icon={{ position: 'right', fileName: 'AngleRight', size: 'Small', fill: '#FFFFFF' }}
            onClick={() => {
              ampli.pvmClickContinueFromTempSaveButton();
              ReactGA.event('pvm_click_sell_tractor_button', {
                isModal: false,
              });
              setIsShowDataCheckModal(true);
            }}
          />
        </ButtonWrapper>
      </Container>

      <PopupConfirmModal
        isShow={isShowDataCheckModal}
        hasCloseButton={false}
        closeModal={() => setIsShowDataCheckModal(false)}
        title={'이 트랙터가 맞나요?'}
        element={<CheckTractor>{`${brand} ${model ?? ''} ${continueWithEngineOutputInPsText}`}</CheckTractor>}
        buttonArray={[
          {
            text: '이어서 신청할게요',
            onClick: handleContinueRegistration,
            color: 'blue',
          },
          {
            text: '닫기',

            onClick: () => {
              ampli.pvmClickSellTractorTempOwnReject();
              ReactGA.event('pvm_click_sell_tractor_temp_own_reject');
              setIsShowDataCheckModal(false);
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey30, #d5d5e3);
`;

const IconBackground = styled.div`
  display: flex;
  padding: var(--radius-medium, 8px);
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-secondary-blue, #e1faff);
`;

const TextSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  flex: 1 0 0;
`;

const BrandAndModel = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* System/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 13.6px */
`;

const Description = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CheckTractor = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
  text-align: center;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;
